import { keymap, EditorView } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { GFM } from '@lezer/markdown';
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching } from '@codemirror/language';
import { closeBrackets } from '@codemirror/autocomplete';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { EditorState } from '@codemirror/state';
import { bearTheme } from './editorTheme';
import { livePreviewPlugin } from './livePreview';
import {
  toggleBold, toggleItalic, toggleStrikethrough,
  toggleBulletList, toggleOrderedList, toggleCheckList,
} from './editorCommands';

/**
 * Upload an image file to the server and insert markdown at cursor position
 */
async function uploadAndInsertImage(view, file) {
  const pos = view.state.selection.main.head;
  // Insert placeholder
  const placeholder = `![업로드 중...](uploading)`;
  view.dispatch({ changes: { from: pos, insert: placeholder } });

  try {
    const apiBase = window.location.hostname === 'localhost'
      ? 'http://localhost:4009'
      : 'https://markdown.ilouli.com';
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${apiBase}/api/uploads/image`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    const data = await res.json();
    if (data.success) {
      const imgUrl = data.data.url.startsWith('http') ? data.data.url : `${apiBase}${data.data.url}`;
      const altText = file.name.replace(/\.[^.]+$/, '');
      const markdown = `![${altText}](${imgUrl})`;
      // Replace placeholder
      const doc = view.state.doc.toString();
      const placeholderPos = doc.indexOf(placeholder);
      if (placeholderPos >= 0) {
        view.dispatch({ changes: { from: placeholderPos, to: placeholderPos + placeholder.length, insert: markdown } });
      }
    } else {
      // Remove placeholder on error
      const doc = view.state.doc.toString();
      const placeholderPos = doc.indexOf(placeholder);
      if (placeholderPos >= 0) {
        view.dispatch({ changes: { from: placeholderPos, to: placeholderPos + placeholder.length, insert: '' } });
      }
    }
  } catch {
    const doc = view.state.doc.toString();
    const placeholderPos = doc.indexOf(placeholder);
    if (placeholderPos >= 0) {
      view.dispatch({ changes: { from: placeholderPos, to: placeholderPos + placeholder.length, insert: '' } });
    }
  }
}

/**
 * Enter키로 목록/인용 자동 계속
 */
function listContinueOnEnter(view) {
  const { state } = view;
  const { head } = state.selection.main;
  const line = state.doc.lineAt(head);
  const text = line.text;

  // 순서 없는 목록: - , * , +
  const bulletMatch = text.match(/^(\s*)([-*+])\s(\[[ x]\]\s)?/);
  if (bulletMatch) {
    const indent = bulletMatch[1];
    const marker = bulletMatch[2];
    const check = bulletMatch[3];
    const afterPrefix = text.slice(bulletMatch[0].length);

    // 비어있으면 목록 종료
    if (!afterPrefix.trim()) {
      view.dispatch({
        changes: { from: line.from, to: line.to, insert: '' },
      });
      return true;
    }

    const newPrefix = check ? `${indent}${marker} [ ] ` : `${indent}${marker} `;
    view.dispatch({
      changes: { from: head, to: head, insert: '\n' + newPrefix },
      selection: { anchor: head + 1 + newPrefix.length },
    });
    return true;
  }

  // 순서 있는 목록: 1. , 2. , ...
  const orderedMatch = text.match(/^(\s*)(\d+)\.\s/);
  if (orderedMatch) {
    const indent = orderedMatch[1];
    const num = parseInt(orderedMatch[2]);
    const afterPrefix = text.slice(orderedMatch[0].length);

    if (!afterPrefix.trim()) {
      view.dispatch({
        changes: { from: line.from, to: line.to, insert: '' },
      });
      return true;
    }

    const newPrefix = `${indent}${num + 1}. `;
    view.dispatch({
      changes: { from: head, to: head, insert: '\n' + newPrefix },
      selection: { anchor: head + 1 + newPrefix.length },
    });
    return true;
  }

  // 인용문: >
  const quoteMatch = text.match(/^(\s*>\s?)/);
  if (quoteMatch) {
    const afterPrefix = text.slice(quoteMatch[0].length);
    if (!afterPrefix.trim()) {
      view.dispatch({
        changes: { from: line.from, to: line.to, insert: '' },
      });
      return true;
    }

    view.dispatch({
      changes: { from: head, to: head, insert: '\n> ' },
      selection: { anchor: head + 3 },
    });
    return true;
  }

  return false;
}

export function createEditorExtensions(onSave, onLinkOpen) {
  const customKeymap = keymap.of([
    {
      key: 'Mod-s',
      run: () => { if (onSave) onSave(); return true; },
    },
    {
      key: 'Mod-b',
      run: (view) => { toggleBold(view); return true; },
    },
    {
      key: 'Mod-i',
      run: (view) => { toggleItalic(view); return true; },
    },
    {
      key: 'Mod-Shift-s',
      run: (view) => { toggleStrikethrough(view); return true; },
    },
    {
      key: 'Mod-Shift-b',
      run: (view) => { toggleBulletList(view); return true; },
    },
    {
      key: 'Mod-Shift-n',
      run: (view) => { toggleOrderedList(view); return true; },
    },
    {
      key: 'Mod-Shift-l',
      run: (view) => { toggleCheckList(view); return true; },
    },
    {
      key: 'Mod-k',
      run: () => {
        if (onLinkOpen) { onLinkOpen(); return true; }
        return false;
      },
    },
    {
      key: 'Enter',
      run: (view) => listContinueOnEnter(view),
    },
  ]);

  // Image drop & paste handler
  const imageDropPaste = EditorView.domEventHandlers({
    drop(event, view) {
      const file = event.dataTransfer?.files?.[0];
      if (file && file.type.startsWith('image/')) {
        event.preventDefault();
        uploadAndInsertImage(view, file);
        return true;
      }
      return false;
    },
    paste(event, view) {
      const items = event.clipboardData?.items;
      if (!items) return false;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) uploadAndInsertImage(view, file);
          return true;
        }
      }
      return false;
    },
  });

  return [
    bearTheme,
    history({ minDepth: 500, newGroupDelay: 300 }),
    markdown({ base: markdownLanguage, codeLanguages: languages, extensions: GFM }),
    syntaxHighlighting(defaultHighlightStyle),
    bracketMatching(),
    closeBrackets(),
    highlightSelectionMatches(),
    livePreviewPlugin,
    customKeymap,
    keymap.of([
      indentWithTab,
      ...defaultKeymap,
      ...historyKeymap,
      ...searchKeymap,
    ]),
    imageDropPaste,
    EditorView.lineWrapping,
    EditorState.allowMultipleSelections.of(true),
  ];
}
